import { createSelector } from '@reduxjs/toolkit'
import { merklebaseAPI } from 'store/api/merklebaseAPI'
import { mapPrices, mapInstrumentsPrices } from 'utils/mappers/prices'

// Base selectors - select RTK Query cache results
// No-arg queries: use default cache key (undefined) so hooks/selectors share the same entry
const selectPricesResult = merklebaseAPI.endpoints.getPrices.select()
const selectInstrumentsPricesResult = merklebaseAPI.endpoints.getInstrumentsPrices.select()

// Select raw data from results
export const selectPricesData = createSelector(
  [selectPricesResult],
  (result) => result?.data ?? []
)

export const selectInstrumentsPricesData = createSelector(
  [selectInstrumentsPricesResult],
  (result) => result?.data ?? []
)

// Memoized derived selectors
export const selectPricesByTicker = createSelector(
  [selectPricesData],
  (prices) => {
    if (!prices?.length) return {}
    return prices.reduce((acc, price) => {
      acc[price.ticker] = price
      return acc
    }, {})
  }
)

export const selectPricesMapByTicker = createSelector(
  [selectPricesData],
  (prices) => new Map((prices ?? []).map((p) => [p.ticker, p]))
)

export const selectParsedInstrumentsPrices = createSelector(
  [selectInstrumentsPricesData],
  (instruments) => (instruments ?? []).map(mapInstrumentsPrices)
)

export const selectInstrumentsPricesByInstrument = createSelector(
  [selectParsedInstrumentsPrices],
  (parsedInstruments) =>
    parsedInstruments.reduce((acc, inst) => {
      acc[inst.id] = inst
      return acc
    }, {})
)

// Factory selectors - for components that need a single value (reduces re-renders)
export const selectPriceImageByTicker = (ticker) =>
  createSelector(
    [selectPricesByTicker],
    (pricesByTicker) => pricesByTicker?.[ticker]?.image
  )

export const selectPriceByTicker = (ticker) =>
  createSelector(
    [selectPricesByTicker],
    (pricesByTicker) => pricesByTicker?.[ticker]
  )

// Mapped/parsed prices (with mapPrices transform)
export const selectParsedPrices = createSelector(
  [selectPricesData],
  (prices) => (prices ?? []).map(mapPrices)
)

export const selectParsedPricesByCurrency = createSelector(
  [selectParsedPrices],
  (parsedPrices) =>
    parsedPrices.reduce((acc, price) => {
      acc[price.ticker] = price
      return acc
    }, {})
)

// Loading and error states
export const selectPricesLoading = createSelector(
  [selectPricesResult],
  (result) => (result?.isLoading || false)
)

export const selectPricesFetching = createSelector(
  [selectPricesResult],
  (result) => (result?.isFetching || false)
)

export const selectPricesError = createSelector(
  [selectPricesResult],
  (result) => result?.error
)

export const selectInstrumentsPricesLoading = createSelector(
  [selectInstrumentsPricesResult],
  (result) => (result?.isLoading || false)
)

export const selectInstrumentsPricesFetching = createSelector(
  [selectInstrumentsPricesResult],
  (result) => (result?.isFetching || false)
)
